/**
 * Report snapshots — a named capture of a report's numbers and controls at one
 * moment (roadmap RP-7; migration 825).
 *
 * A snapshot has an address, /prioritization/reports/snapshots/:id, readable by
 * signed-in members of the workspace (RLS). Sharing is the address; there is no
 * anonymous link (owner ruling 2026-10-13).
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import type { Json } from '../types/database.generated';
import { getCurrentWorkspaceId } from './supabaseSync';

export interface SnapshotStat {
  label: string;
  value: string | number;
  sub?: string;
}

export interface SnapshotSeriesRow {
  label: string;
  value: number;
  /** Where the bar leads — the item list pre-filtered, or a detail page. */
  href?: string;
  /** Series colour at capture, already a concrete value. */
  color?: string;
}

export interface SnapshotSeries {
  title: string;
  description?: string;
  rows: SnapshotSeriesRow[];
}

/** The numbers a report hands over when a snapshot is taken. */
export interface ReportSnapshotSummary {
  stats: SnapshotStat[];
  series: SnapshotSeries[];
}

/** The controls that produced the numbers — enough to reopen the live report. */
export interface ReportSnapshotConfig {
  /** Query string of the report controls (range, segment …), no leading `?`. */
  search: string;
  rangeLabel: string;
  segmentLabel: string | null;
  /** The live report's path, without query. */
  reportPath: string;
  /** The report's display name at capture. */
  reportName: string;
  /** For a custom report: the saved query id and the statement it ran. */
  savedReportId?: string | null;
  statement?: string | null;
}

export interface ReportSnapshot {
  id: string;
  workspaceId: string;
  reportKey: string;
  name: string;
  note: string | null;
  config: ReportSnapshotConfig;
  summary: ReportSnapshotSummary;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string;
}

export function snapshotPath(id: string): string {
  return `/prioritization/reports/snapshots/${id}`;
}

export function snapshotEmbedPath(id: string): string {
  return `/embed/reports/snapshots/${id}`;
}

type Row = {
  id: string;
  workspace_id: string;
  report_key: string;
  name: string;
  note: string | null;
  config: unknown;
  summary: unknown;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
};

function snapshotFromRow(r: Row): ReportSnapshot {
  const config = (r.config && typeof r.config === 'object' ? r.config : {}) as Partial<ReportSnapshotConfig>;
  const summary = (r.summary && typeof r.summary === 'object' ? r.summary : {}) as Partial<ReportSnapshotSummary>;
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    reportKey: r.report_key,
    name: r.name,
    note: r.note,
    config: {
      search: config.search ?? '',
      rangeLabel: config.rangeLabel ?? 'All time',
      segmentLabel: config.segmentLabel ?? null,
      reportPath: config.reportPath ?? '/prioritization/reports',
      reportName: config.reportName ?? r.report_key,
      savedReportId: config.savedReportId ?? null,
      statement: config.statement ?? null,
    },
    summary: { stats: summary.stats ?? [], series: summary.series ?? [] },
    createdBy: r.created_by,
    createdByName: r.created_by_name,
    createdAt: r.created_at,
  };
}

export interface CreateSnapshotInput {
  reportKey: string;
  name: string;
  note?: string;
  config: ReportSnapshotConfig;
  summary: ReportSnapshotSummary;
  createdByName?: string | null;
}

export async function createReportSnapshot(input: CreateSnapshotInput): Promise<{ snapshot: ReportSnapshot | null; error: string | null }> {
  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return { snapshot: null, error: 'No workspace is open.' };
  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('report_snapshots')
    .insert({
      workspace_id: workspaceId,
      report_key: input.reportKey,
      name: input.name,
      note: input.note ?? null,
      config: input.config as unknown as NonNullable<Json>,
      summary: input.summary as unknown as NonNullable<Json>,
      created_by_name: input.createdByName ?? null,
    })
    .select('*')
    .single();
  if (error || !data) return { snapshot: null, error: error?.message ?? 'The snapshot was not saved.' };
  return { snapshot: snapshotFromRow(data as Row), error: null };
}

export async function fetchReportSnapshot(id: string): Promise<{ snapshot: ReportSnapshot | null; error: string | null }> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db.from('report_snapshots').select('*').eq('id', id).maybeSingle();
  if (error) return { snapshot: null, error: error.message };
  if (!data) return { snapshot: null, error: null };
  return { snapshot: snapshotFromRow(data as Row), error: null };
}

export async function listReportSnapshots(opts: { reportKey?: string; limit?: number } = {}): Promise<ReportSnapshot[]> {
  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return [];
  const db = getTypedSupabaseClient();
  let q = db
    .from('report_snapshots')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: false })
    .limit(opts.limit ?? 50);
  if (opts.reportKey) q = q.eq('report_key', opts.reportKey);
  const { data, error } = await q;
  if (error || !data) return [];
  return (data as Row[]).map(snapshotFromRow);
}

export async function deleteReportSnapshot(id: string): Promise<{ error: string | null }> {
  const db = getTypedSupabaseClient();
  const { error } = await db.from('report_snapshots').delete().eq('id', id);
  return { error: error?.message ?? null };
}
